import { createClient, PostgrestError } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_DEFAULT_KEY;

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables');
}

// Crear cliente de Supabase con sesiones configuradas para durar 2 horas (7200 segundos)
export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storageKey: 'disolgua-auth',
    flowType: 'pkce',
  },
  global: {
    headers: {
      'X-Client-Info': 'supabase-js-web',
    }
  },
  realtime: {
    params: {
      eventsPerSecond: 2
    }
  }
});

/**
 * Ejecuta una operación de Supabase con reintentos automáticos en caso de errores de conexión
 * @param operation Función que realiza la operación de Supabase
 * @param maxRetries Número máximo de reintentos (por defecto 3)
 * @param delay Retraso inicial entre reintentos en ms (por defecto 1000)
 * @returns El resultado de la operación
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  maxRetries = 3,
  delay = 1000
): Promise<T> {
  let lastError: any;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Intentar la operación
      return await operation();
    } catch (error) {
      console.warn(`Intento ${attempt + 1}/${maxRetries + 1} falló:`, error);
      lastError = error;
      
      // Si es el último intento, no esperar
      if (attempt === maxRetries) break;
      
      // Esperar antes del siguiente intento (con backoff exponencial)
      const backoffDelay = delay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, backoffDelay));
    }
  }
  
  // Si llegamos aquí, todos los intentos fallaron
  console.error(`Todos los intentos fallaron después de ${maxRetries + 1} intentos`);
  throw lastError;
}

/**
 * Verifica si hay conexión a internet
 * @returns Promise que resuelve a true si hay conexión, false si no
 */
export async function checkConnection(): Promise<boolean> {
  try {
    // Método 1: Intentar hacer una petición simple al endpoint de Supabase
    try {
      const response = await fetch(`${supabaseUrl}/rest/v1/`, {
        method: 'HEAD',
        headers: {
          'apikey': supabaseKey
        }
      });
      
      if (response.ok) {
        console.log('Conexión verificada mediante endpoint REST');
        return true;
      }
    } catch (e) {
      console.warn('Error al verificar conexión mediante endpoint REST:', e);
    }
    
    // Método 2: Intentar consultar una tabla que sabemos que existe
    try {
      console.log('Intentando verificar conexión mediante consulta a tabla...');
      const { error } = await supabase
        .from('profiles')
        .select('id')
        .limit(1);
      
      if (!error) {
        console.log('Conexión verificada mediante consulta a tabla profiles');
        return true;
      }
    } catch (e) {
      console.warn('Error al verificar conexión mediante consulta a tabla:', e);
    }
    
    // Si llegamos aquí, ambos métodos fallaron
    console.error('No se pudo establecer conexión con Supabase');
    return false;
  } catch (error) {
    console.error('Error general al verificar conexión:', error);
    return false;
  }
}

/**
 * Ejecuta una operación solo si hay conexión a internet
 * @param operation Función que realiza la operación
 * @param onNoConnection Función a ejecutar si no hay conexión
 * @returns El resultado de la operación o undefined si no hay conexión
 */
export async function withConnectionCheck<T>(
  operation: () => Promise<T>,
  onNoConnection?: () => void
): Promise<T | undefined> {
  try {
    console.log('Verificando conexión a internet...');
    const isConnected = await checkConnection();
    
    if (!isConnected) {
      console.warn('No hay conexión a internet');
      if (onNoConnection) {
        onNoConnection();
      } else {
        console.warn('No se proporcionó una función para manejar la falta de conexión');
      }
      return undefined;
    }
    
    console.log('Conexión verificada, ejecutando operación...');
    return await operation();
  } catch (error) {
    console.error('Error durante la verificación de conexión o la operación:', error);
    throw error;
  }
}

/**
 * Implementa un sistema de caché avanzado para consultas frecuentes
 * @param key Clave única de caché
 * @param fetchData Función que realiza la consulta a Supabase
 * @param ttlSeconds Tiempo de vida en segundos (por defecto 60 segundos)
 * @returns Los datos de la caché o de la consulta
 */
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  promise?: Promise<T>; // Para evitar llamadas concurrentes duplicadas
}

const queryCache = new Map<string, CacheEntry<any>>();
const pendingRequests = new Map<string, Promise<any>>();

export async function withCache<T>(
  key: string,
  fetchData: () => Promise<T>,
  ttlSeconds = 60
): Promise<T> {
  const now = Date.now();
  const cached = queryCache.get(key);

  // Si hay datos en caché y no han expirado, devolverlos
  if (cached && (now - cached.timestamp < ttlSeconds * 1000) && cached.data !== undefined) {
    console.log(`Cache hit for key: ${key}`);
    return cached.data;
  }

  // Si hay una solicitud pendiente para esta clave, esperar a que termine
  if (pendingRequests.has(key)) {
    console.log(`Waiting for pending request for key: ${key}`);
    return pendingRequests.get(key)!;
  }

  // Crear una nueva promesa para esta solicitud
  const fetchPromise = fetchData().then(data => {
    // Guardar en caché
    queryCache.set(key, { data, timestamp: now });
    // Limpiar la promesa pendiente
    pendingRequests.delete(key);
    return data;
  }).catch(error => {
    // En caso de error, limpiar la promesa pendiente pero no guardar en caché
    pendingRequests.delete(key);
    throw error;
  });

  // Guardar la promesa pendiente
  pendingRequests.set(key, fetchPromise);

  return fetchPromise;
}

/**
 * Realiza una consulta a Supabase optimizada para colecciones de datos grandes
 * @param tableName Nombre de la tabla
 * @param options Opciones de la consulta
 * @returns Resultados paginados con metadatos
 */
export async function optimizedQuery<T>(
  tableName: string, 
  options: {
    select?: string;
    filters?: Record<string, any>;
    page?: number;
    limit?: number;
    orderBy?: { column: string; ascending?: boolean };
    useCache?: boolean;
    cacheTTL?: number;
  } = {}
  ): Promise<{ data: T[]; count: number; error: PostgrestError | null }> {
  const {
    select = '*',
    filters = {},
    page = 1,
    limit = 20,
    orderBy,
    useCache = false,
    cacheTTL = 60
  } = options;
  
  // Calcular el offset para la paginación
  const offset = (page - 1) * limit;
  
  // Construir una clave de caché única si se usa caché
  const getCacheKey = () => {
    const parts = [
      tableName,
      select,
      JSON.stringify(filters),
      offset.toString(),
      limit.toString(),
      orderBy ? `${orderBy.column}-${orderBy.ascending ? 'asc' : 'desc'}` : ''
    ];
    return parts.join('|');
  };
  
  // Función para realizar la consulta
  const fetchFunction = async () => {
    try {
      // Iniciar la consulta
      let query = supabase
        .from(tableName)
        .select(select, { count: 'exact' });
      
      // Aplicar filtros
      for (const [key, value] of Object.entries(filters)) {
        if (key.includes('.')) {
          // Manejar filtros anidados como category.id
          const [table, field] = key.split('.');
          query = query.eq(field, value);
        } else if (Array.isArray(value)) {
          // Manejar arrays para consultas IN
          query = query.in(key, value);
        } else if (typeof value === 'object' && value !== null) {
          // Manejar operadores personalizados como { gt: 100 }
          for (const [op, val] of Object.entries(value)) {
            switch (op) {
              case 'eq': query = query.eq(key, val); break;
              case 'neq': query = query.neq(key, val); break;
              case 'gt': query = query.gt(key, val); break;
              case 'gte': query = query.gte(key, val); break;
              case 'lt': query = query.lt(key, val); break;
              case 'lte': query = query.lte(key, val); break;
              case 'like': query = query.like(key, val as string); break;
              case 'ilike': query = query.ilike(key, val as string); break;
              case 'in': query = query.in(key, val as any[]); break;
              case 'is': query = query.is(key, val); break;
            }
          }
        } else {
          // Filtro de igualdad simple
          query = query.eq(key, value);
        }
      }
      
      // Aplicar ordenamiento si se especificó
      if (orderBy) {
        query = query.order(orderBy.column, { ascending: orderBy.ascending ?? true });
      }
      
      // Aplicar paginación
      query = query.range(offset, offset + limit - 1);
      
      // Ejecutar la consulta
      const { data, error, count } = await query;
      
      return { 
        data: data || [], 
        count: count || 0, 
        error 
      };
    } catch (error) {
      console.error(`Error en optimizedQuery para ${tableName}:`, error);
      return { data: [], count: 0, error };
    }
  };
  
  // Usar caché si se especificó
  if (useCache) {
    const cacheKey = getCacheKey();
    return withCache(cacheKey, fetchFunction, cacheTTL);
  }
  
  return fetchFunction();
}

/**
 * Limpia la caché de consultas
 * @param key Clave específica a limpiar (opcional, si no se proporciona limpia toda la caché)
 */
export function clearQueryCache(key?: string): void {
  if (key) {
    queryCache.delete(key);
    pendingRequests.delete(key);
    console.log(`Caché limpiada para: ${key}`);
  } else {
    queryCache.clear();
    pendingRequests.clear();
    console.log('Caché de consultas limpiada completamente');
  }
}

/**
 * Obtiene estadísticas de la caché para debugging
 */
export function getCacheStats() {
  const now = Date.now();
  let validEntries = 0;
  let expiredEntries = 0;
  let pendingCount = pendingRequests.size;

  for (const [key, entry] of queryCache.entries()) {
    if (now - entry.timestamp < 60 * 1000) { // 60 segundos TTL por defecto
      validEntries++;
    } else {
      expiredEntries++;
    }
  }

  return {
    totalEntries: queryCache.size,
    validEntries,
    expiredEntries,
    pendingRequests: pendingCount
  };
}

/**
 * Limpia entradas expiradas de la caché automáticamente
 */
export function cleanupExpiredCache(): void {
  const now = Date.now();
  const defaultTTL = 60 * 1000; // 60 segundos
  let cleaned = 0;

  for (const [key, entry] of queryCache.entries()) {
    if (now - entry.timestamp >= defaultTTL) {
      queryCache.delete(key);
      cleaned++;
    }
  }

  if (cleaned > 0) {
    console.log(`Limpieza automática: ${cleaned} entradas expiradas eliminadas`);
  }
}

// Ejecutar limpieza automática cada 5 minutos
if (typeof window !== 'undefined') {
  setInterval(cleanupExpiredCache, 5 * 60 * 1000);
}